import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router';

/**
 * Applies a URL parameter when the page is opened with it — deep links from
 * the search (e.g. `?item=12`, `?user=4`, `?event=3`) — and then removes it
 * from the address: the same link works again, and nothing stays in the
 * history or reaches the access logs on a reload.
 *
 * `ready`: wait until the page has its data (e.g. the list the id refers to).
 * `apply` may return `false` to keep the parameter and try again the next
 * time `ready` becomes true (e.g. the list of another event is still shown).
 */
export function useUrlParam(
  name: string,
  apply: (value: string) => boolean | void,
  { ready = true }: { ready?: boolean } = {},
) {
  const [params, setParams] = useSearchParams();
  const value = params.get(name);
  const applyRef = useRef(apply);
  applyRef.current = apply;

  useEffect(() => {
    if (!value || !ready) return;
    if (applyRef.current(value) === false) return;
    setParams(prev => {
      const next = new URLSearchParams(prev);
      next.delete(name);
      return next;
    }, { replace: true });
  }, [value, ready, name, setParams]);
}
